import React, { useEffect, useMemo, useReducer } from "react";
import {
  feederTwinScenarios,
  createTwinScenarioState,
  transitionTwinScenario,
  getTwinScenarioSnapshot,
} from "./FeederTwinScenarios";
import "./greenhouseLearningPanel.css";

const learningStages = [
  { key: "observation", label: "Beobachtung", question: "Was ist im Modell zu sehen?" },
  { key: "decision", label: "Entscheidung", question: "Wie wird die Situation eingeordnet?" },
  { key: "localExecution", label: "Lokale Ausführung", question: "Was passiert in diesem Beispiel vor Ort?" },
  { key: "evidenceBoundary", label: "Nachweisgrenze", question: "Was können wir daraus nicht schließen?" },
];

/** Browser-local teaching only. A snapshot describes an illustration, never a device command. */
export default function GreenhouseLearningPanel({
  onSnapshotChange,
  onSelectComponent,
  id = "feeder-learning",
}) {
  const [state, dispatch] = useReducer(
    transitionTwinScenario,
    undefined,
    () => createTwinScenarioState()
  );
  const snapshot = useMemo(() => getTwinScenarioSnapshot(state), [state]);
  const { scenario, step, stepNumber, totalSteps, isStarted, isComplete } = snapshot;
  const visibleSteps = isStarted ? scenario.steps.slice(0, stepNumber) : [];

  useEffect(() => {
    if (onSnapshotChange) onSnapshotChange(snapshot);
  }, [snapshot, onSnapshotChange]);

  return (
    <section className="feeder-learning" aria-labelledby={`${id}-title`}>
      <div className="feeder-learning-heading">
        <div>
          <p className="feeder-learning-kicker">Dein Lernlabor</p>
          <h2 id={`${id}-title`}>Was passiert, wenn …?</h2>
          <p>
            Wähle eine Situation und gehe sie in deinem Tempo durch. Jeder Schritt
            verbindet Beobachtung, Entscheidung und die lokale Ausführung mit dem,
            was noch geprüft werden müsste.
          </p>
        </div>
        <p className="feeder-learning-disclosure">
          Simulation · keine Livewerte oder Schaltbefehle
        </p>
      </div>

      <div className="feeder-learning-workbench">
        <div className="feeder-learning-setup">
          <label htmlFor={`${id}-scenario`}>Welche Situation möchtest du verstehen?</label>
          <select
            id={`${id}-scenario`}
            value={scenario.id}
            onChange={(event) => dispatch({ type: "select", scenarioId: event.target.value })}
          >
            {feederTwinScenarios.map((item) => (
              <option key={item.id} value={item.id}>{item.title}</option>
            ))}
          </select>
          <p className="feeder-learning-summary">{scenario.summary}</p>
          <p className="feeder-learning-evidence">{scenario.evidenceLevel}</p>
          <p className="feeder-learning-local-note">
            Alle Ereignisse sind vorbereitete Lernbeispiele. Sie werden nur hier im
            Browser durchgespielt – ohne Verbindung zu einer Anlage. Das Beispiel
            bestätigt keine geprüfte Schutzfunktion oder Betriebssicherheit.
          </p>
        </div>

        <div className="feeder-learning-session">
          <div className="feeder-learning-status" role="status" aria-live="polite" aria-atomic="true">
            <span>{isStarted ? `Schritt ${stepNumber} von ${totalSteps}` : "Vorschau · noch nicht gestartet"}</span>
            <strong>{isComplete ? "Lernfolge abgeschlossen" : isStarted ? "Du bestimmst das Tempo" : `${totalSteps} Schritte zum Entdecken`}</strong>
          </div>
          <div className="feeder-learning-progress" aria-hidden="true">
            {scenario.steps.map((item, index) => (
              <span
                key={item.id}
                className={isStarted && index < stepNumber ? "is-reached" : ""}
              />
            ))}
          </div>
          <p className="feeder-learning-next-label">{isStarted ? "Im Modell erklärt" : "Damit beginnt die Lernfolge"}</p>
          <h3 className="feeder-learning-step-title">{step.title}</h3>
          {!isStarted && <p className="feeder-learning-preview">Mit „Szenario starten“ wird der erste Schritt sichtbar. Es läuft nichts automatisch weiter.</p>}
          <div className="feeder-learning-controls" aria-label="Lernfolge steuern">
            <button type="button" className="feeder-learning-primary" disabled={isStarted} onClick={() => dispatch({ type: "start" })}>
              Szenario starten
            </button>
            <button type="button" className="feeder-learning-primary" disabled={!isStarted || isComplete} onClick={() => dispatch({ type: "next" })}>
              Nächster Lernschritt <span aria-hidden="true">→</span>
            </button>
            <button type="button" className="feeder-learning-reset" disabled={!isStarted} onClick={() => dispatch({ type: "reset" })}>
              Zurücksetzen
            </button>
          </div>
          {onSelectComponent && isStarted && (
            <button
              type="button"
              className="feeder-learning-component"
              onClick={() => onSelectComponent(step.focusPart)}
            >
              Passendes Bauteil im 3D-Modell ansehen <span aria-hidden="true">↗</span>
            </button>
          )}
        </div>
      </div>

      {isStarted && (
        <div className="feeder-learning-states" aria-label="Simulierte Zustände dieses Lernschritts">
          <p><span>Lokale Steuerung · simuliert</span><strong>{step.localState}</strong></p>
          <p><span>Portalverbindung · simuliert</span><strong>{step.portalState}</strong></p>
          <p><span>Messqualität · simuliert</span><strong>{step.measurementQuality}</strong></p>
        </div>
      )}

      <div className="feeder-learning-events">
        <h3>Die Ereignisse nachvollziehen</h3>
        <p className="feeder-learning-events-intro">
          Beobachtung → Entscheidung → lokale Ausführung → Nachweisgrenze.
          Bereits besuchte Schritte kannst du wieder aufklappen.
        </p>
        {!isStarted ? (
          <p className="feeder-learning-empty">Noch keine simulierten Ereignisse. Starte die ausgewählte Lernfolge, um sie Schritt für Schritt zu erkunden.</p>
        ) : (
          <ol aria-label="Simulierte Ereignisfolge" className="feeder-learning-timeline">
            {visibleSteps.map((item, index) => {
              const current = index === stepNumber - 1;
              return (
                <li key={`${scenario.id}-${item.id}`} className={current ? "is-current" : ""}>
                  <details open={current}>
                    <summary>
                      <span className="feeder-learning-event-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                      <span>{item.title}<small>{current ? "Aktueller Lernschritt" : "Bereits durchgespielt"} · Simulation</small></span>
                      <span className="feeder-learning-expand" aria-hidden="true">+</span>
                    </summary>
                    <dl>
                      {learningStages.map((stage, stageIndex) => (
                        <div key={stage.key} className={`feeder-learning-stage feeder-learning-stage-${stage.key}`}>
                          <dt><span aria-hidden="true">{stageIndex + 1}</span>{stage.label}</dt>
                          <dd><small>{stage.question}</small><p>{item[stage.key]}</p></dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
